import { ref, computed } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "./useMyFetch";
import type { Role, Access } from "@/types";
import { captureEvent } from "@/lib/analytics";

interface RolePayload {
  name?: string;
  description?: string;
  access_ids: number[];
}

export const useRolesStore = defineStore("roles", () => {
  const roles = ref<Role[]>([]);
  const accesses = ref<Access[]>([]);

  const roleOptions = computed(() => roles.value.map(({ id, name }) => ({ label: name, value: id })));

  async function fetch() {
    const { data, error } = await useMyFetch("/roles").json<{ roles: Role[] }>();
    if (error.value) throw error.value;
    roles.value = data.value?.roles ?? [];
  }

  async function fetchAccesses() {
    const { data, error } = await useMyFetch("/accesses").json<{ accesses: Access[] }>();
    if (error.value) throw error.value;
    accesses.value = data.value?.accesses ?? [];
  }

  async function createRole(payload: RolePayload) {
    const { data, error } = await useMyFetch("/roles").post({ role: payload }).json<Role>();
    if (error.value) throw error.value;

    captureEvent("role_created", { role_id: data.value?.id });

    if (data.value) roles.value.push(data.value);
    return data.value;
  }

  async function updateRole(id: number, payload: RolePayload) {
    const { data, error } = await useMyFetch(`/roles/${id}`).patch({ role: payload }).json<Role>();
    if (error.value) throw error.value;

    captureEvent("role_updated", { role_id: id });

    const index = roles.value.findIndex((role) => role.id === id);
    if (index !== -1 && data.value) roles.value[index] = data.value;
    return data.value;
  }

  async function deleteRole(id: number) {
    const { error } = await useMyFetch(`/roles/${id}`).delete();
    if (error.value) throw error.value;

    captureEvent("role_deleted", { role_id: id });

    roles.value = roles.value.filter((role) => role.id !== id);
  }

  function roleByKey(key: string) {
    return roles.value.find((role) => role.key === key);
  }

  return {
    roles,
    accesses,
    roleOptions,
    fetch,
    fetchAccesses,
    createRole,
    updateRole,
    deleteRole,
    roleByKey,
  };
});
